import { useState, useMemo, useEffect, useCallback } from "react";
import { NavHeader } from "@/components/layout/nav-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, Instagram, Upload, Eye, Youtube, Music, Music2, Check, XCircle, Calendar, Trash2, XOctagon, Heart } from "lucide-react";
import { useBookings } from "@/hooks/use-bookings";
import { VenueTimeFormats, parseVenueTime } from "@/utils/timezone";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { BookingLifecycleStatus, statusConfig } from "@/types/booking";
import { useQueryClient } from "@tanstack/react-query";
import { UploadVideoModal } from "@/components/booking/upload-video-modal";
import { getMultipartUploader } from "@/utils/multipart-upload";
import { useLocation, useSearch } from "wouter";

interface Booking {
  id: string | number;
  dj: { 
    id: string | number; 
    displayName?: string;
    email?: string;
    phoneNumber?: string;
    instagramHandle?: string;
    soundCloudLink?: string;
    mixLink?: string;
    spotifyPlaylistLink?: string;
  };
  timeSlot: {
    startTime: string | Date;
    endTime: string | Date;
  };
  status: string;
  lifecycleStatus?: BookingLifecycleStatus | null;
  performedAt?: string | Date | null;
  notes?: string;
  duration?: number;
  causeLink?: string | null;  // Direct cause link on booking
  causeName?: string | null;  // Cause name on booking
  set?: { 
    videoUrl?: string | null;
    publicVideoUrl?: string | null;
    causeLink?: string | null;
    causeLabel?: string | null;
    status?: string;
    artistApprovedAt?: string | Date | null;
    postedAt?: string | Date | null;
    uploadStatus?: "pending" | "uploading" | "completed" | "failed" | null;
    uploadProgress?: number | null;
    episodeNumber?: string | null;
    qrCodeLink?: string | null;
  } | null;
  createdAt?: string;
}

export default function AdminBookings() {
  const { data: bookingsData, isLoading, error } = useBookings();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const searchParams = useSearch();
  
  // Parse URL parameters
  const urlParams = new URLSearchParams(searchParams);
  
  // Initialize state from URL parameters or defaults
  const [mainTab, setMainTab] = useState<"bookings" | "recordings">(
    (urlParams.get("tab") as "bookings" | "recordings") || "bookings"
  );
  const [bookingsTab, setBookingsTab] = useState<"upcoming" | "past" | "all">(
    (urlParams.get("bookingsTab") as "upcoming" | "past" | "all") || "upcoming"
  );
  const [recordingsTab, setRecordingsTab] = useState<"all" | "awaiting" | "artist-review" | "approved" | "posted">(
    (urlParams.get("recordingsTab") as "all" | "awaiting" | "artist-review" | "approved" | "posted") || "all"
  );
  const [searchQuery, setSearchQuery] = useState(urlParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(urlParams.get("status") || "all");
  const [sortOrder, setSortOrder] = useState(urlParams.get("sort") || "soonest");
  
  // Upload modal state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedBookingForUpload, setSelectedBookingForUpload] = useState<Booking | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Track uploads per booking to persist state when modal closes
  const [activeUploads, setActiveUploads] = useState<Record<string, { progress: number }>>({});
  
  // Video link modal state
  const [videoLinkModalOpen, setVideoLinkModalOpen] = useState(false);
  const [selectedBookingForVideoLink, setSelectedBookingForVideoLink] = useState<Booking | null>(null);
  
  // Loading states for actions
  const [loadingActions, setLoadingActions] = useState<Record<string, boolean>>({});
  
  // Notes modal state
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [selectedBookingNotes, setSelectedBookingNotes] = useState<{ djName: string; notes: string }>({ djName: "", notes: "" });
  
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    action: 'approve' | 'reject' | 'delete' | null;
    bookingId: string | number | null;
    djName: string;
    bookingTime: string;
  }>({
    open: false,
    action: null,
    bookingId: null,
    djName: "",
    bookingTime: ""
  });

  const now = new Date();

  // Function to update URL parameters
  const updateUrlParams = useCallback((updates: Record<string, string>) => {
    const params = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== "all" && value !== "") {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });
    
    const newSearch = params.toString();
    setLocation(`/admin/bookings${newSearch ? `?${newSearch}` : ''}`, { replace: true });
  }, [searchParams, setLocation]);

  // Update URL when state changes
  useEffect(() => {
    updateUrlParams({
      tab: mainTab,
      bookingsTab: bookingsTab,
      recordingsTab: recordingsTab,
      search: searchQuery,
      status: statusFilter,
      sort: sortOrder
    });
  }, [mainTab, bookingsTab, recordingsTab, searchQuery, statusFilter, sortOrder, updateUrlParams]);

  // Filter bookings based on search and filters
  const filteredBookings = useMemo(() => {
    if (!bookingsData) return [];
    
    let filtered = [...bookingsData];
    
    // Search filter
    if (searchQuery) {
      filtered = filtered.filter((booking: Booking) => {
        const djName = booking.dj.displayName?.toLowerCase() || "";
        const djEmail = booking.dj.email?.toLowerCase() || "";
        const query = searchQuery.toLowerCase();
        return djName.includes(query) || djEmail.includes(query);
      });
    }

    // Main tab filter - Use lifecycle status if available, fallback to old logic
    if (mainTab === "bookings") {
      // Pre-set bookings - filter by lifecycle status
      filtered = filtered.filter((booking: Booking) => {
        if (booking.lifecycleStatus) {
          return [
            BookingLifecycleStatus.PENDING_APPROVAL,
            BookingLifecycleStatus.SCHEDULED,
            BookingLifecycleStatus.REJECTED,
            BookingLifecycleStatus.CANCELLED,
            BookingLifecycleStatus.NO_SHOW
          ].includes(booking.lifecycleStatus);
        }
        // Fallback to old logic - show if no video and not completed/posted
        return !booking.set?.videoUrl && booking.status !== 'completed' && booking.status !== 'posted';
      });
      
      // Sub-tab filter
      if (bookingsTab === "upcoming") {
        filtered = filtered.filter((booking: Booking) => parseVenueTime(booking.timeSlot.startTime) >= now);
      } else if (bookingsTab === "past") {
        filtered = filtered.filter((booking: Booking) => parseVenueTime(booking.timeSlot.startTime) < now);
      }
    } else {
      // Recordings - everything else (video-related statuses)
      filtered = filtered.filter((booking: Booking) => {
        if (booking.lifecycleStatus) {
          return [
            BookingLifecycleStatus.AWAITING_VIDEO,
            BookingLifecycleStatus.ARTIST_REVIEW,
            BookingLifecycleStatus.READY_TO_POST,
            BookingLifecycleStatus.POSTED
          ].includes(booking.lifecycleStatus);
        }
        // Fallback to old logic - show if has video or is past approved booking
        const isPast = parseVenueTime(booking.timeSlot.startTime) < now;
        return booking.set?.videoUrl || (isPast && booking.status === 'approved');
      });
      
      // Sub-tab filter for recordings
      if (recordingsTab === "awaiting") {
        filtered = filtered.filter((booking: Booking) => 
          booking.lifecycleStatus === BookingLifecycleStatus.AWAITING_VIDEO ||
          (!booking.lifecycleStatus && booking.set?.status === "awaiting_video")
        );
      } else if (recordingsTab === "artist-review") {
        filtered = filtered.filter((booking: Booking) => 
          booking.lifecycleStatus === BookingLifecycleStatus.ARTIST_REVIEW ||
          (!booking.lifecycleStatus && booking.set?.status === "artist_review")
        );
      } else if (recordingsTab === "approved") {
        filtered = filtered.filter((booking: Booking) => 
          booking.lifecycleStatus === BookingLifecycleStatus.READY_TO_POST ||
          (!booking.lifecycleStatus && booking.set?.status === "approved")
        );
      } else if (recordingsTab === "posted") {
        filtered = filtered.filter((booking: Booking) => 
          booking.lifecycleStatus === BookingLifecycleStatus.POSTED ||
          (!booking.lifecycleStatus && booking.set?.status === "posted")
        );
      }
    }

    // Status filter - applies to both bookings and recordings tabs
    if (statusFilter !== "all") {
      filtered = filtered.filter((booking: Booking) => {
        // Map the select filter values to lifecycle statuses
        switch (statusFilter) {
          case "pending":
            return booking.lifecycleStatus === BookingLifecycleStatus.PENDING_APPROVAL;
          case "scheduled":
            return booking.lifecycleStatus === BookingLifecycleStatus.SCHEDULED;
          case "completed":
            // For completed, we consider multiple statuses
            return [
              BookingLifecycleStatus.AWAITING_VIDEO,
              BookingLifecycleStatus.ARTIST_REVIEW,
              BookingLifecycleStatus.READY_TO_POST,
              BookingLifecycleStatus.POSTED
            ].includes(booking.lifecycleStatus as BookingLifecycleStatus);
          case "rejected":
            return booking.lifecycleStatus === BookingLifecycleStatus.REJECTED;
          default:
            return true;
        }
      });
    }

    // Sort
    filtered.sort((a, b) => {
      const dateA = parseVenueTime(a.timeSlot.startTime).getTime();
      const dateB = parseVenueTime(b.timeSlot.startTime).getTime();
      // For soonest: dates closer to now come first
      // For furthest: dates further from now come first
      const nowTime = new Date().getTime();
      const diffA = Math.abs(dateA - nowTime);
      const diffB = Math.abs(dateB - nowTime);
      return sortOrder === "soonest" ? diffA - diffB : diffB - diffA;
    });

    return filtered;
  }, [bookingsData, searchQuery, mainTab, bookingsTab, recordingsTab, statusFilter, sortOrder, now]);

  const getBookingStatus = (booking: Booking) => {
    // Use lifecycle status if available
    if (booking.lifecycleStatus) {
      const config = statusConfig[booking.lifecycleStatus];
      if (config) return config;
    }
    
    // Fallback to old logic
    const isPast = parseVenueTime(booking.timeSlot.startTime) < now;
    
    if (booking.status === "pending") return { label: "Pending Review", color: "bg-orange-500" };
    if (booking.status === "approved" && !isPast) return { label: "Scheduled", color: "bg-green-500" };
    if (booking.status === "rejected") return { label: "Rejected", color: "bg-red-500" };
    if (booking.status === "completed") return { label: "Completed", color: "bg-green-500" };
    
    return { label: booking.status, color: "bg-gray-500" };
  };

  const getRecordingStatus = (booking: Booking) => {
    // Use lifecycle status if available
    if (booking.lifecycleStatus) {
      const config = statusConfig[booking.lifecycleStatus];
      if (config) return config;
    }
    
    // Fallback to old logic
    if (!booking.set?.videoUrl) return { label: "Awaiting Video", color: "bg-gray-500" };
    
    switch (booking.set.status) {
      case "artist_review":
        return { label: "Artist Review", color: "bg-orange-500" };
      case "approved":
        return { label: "Ready to Post", color: "bg-transparent border border-green-500 text-green-500" };
      case "posted":
        return { label: "Posted", color: "bg-green-500" };
      default:
        return { label: "Awaiting Video", color: "bg-gray-500" };
    }
  };

  const copyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    toast({
      title: "Link copied",
      description: "The link has been copied to your clipboard.",
    });
  };

  const ensureHttps = (url: string): string => {
    if (!url) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  };

  const openLink = (url: string | undefined) => {
    if (!url) return;
    window.open(ensureHttps(url), '_blank');
  };

  const getInstagramUrl = (handle: string | undefined): string => {
    if (!handle) return '';
    // Remove @ if present
    const cleanHandle = handle.startsWith('@') ? handle.substring(1) : handle;
    return `https://instagram.com/${cleanHandle}`;
  };

  const handleVideoUpload = async (bookingId: number, videoFile: File, setlist: string) => {
    setIsUploading(true);
    setUploadProgress(0);
    
    // Track this upload
    setActiveUploads(prev => ({ ...prev, [bookingId]: { progress: 0 } }));

    // Optimistic update
    const previousBookings = queryClient.getQueryData(["bookings"]);
    queryClient.setQueryData(["bookings"], (old: any) => {
      if (!old) return old;
      return old.map((b: Booking) => {
        if (String(b.id) === String(bookingId)) {
          return {
            ...b,
            status: "ready",
            lifecycleStatus: BookingLifecycleStatus.ARTIST_REVIEW,
            set: {
              ...b.set,
              videoUrl: null, // Don't set a fake URL during upload
              status: "pending",
              uploadStatus: "uploading",
              uploadProgress: 0
            }
          };
        }
        return b;
      });
    });

    try {
      const uploader = getMultipartUploader();
      await uploader.uploadFile(videoFile, { type: "booking", bookingId: Number(bookingId) }, setlist, {
        onProgress: (progress) => {
          setUploadProgress(progress);
          setActiveUploads(prev => ({ ...prev, [bookingId]: { progress } }));
          
          // Update the booking data with progress
          queryClient.setQueryData(["bookings"], (old: any) => {
            if (!old) return old;
            return old.map((b: Booking) => {
              if (String(b.id) === String(bookingId)) {
                return {
                  ...b,
                  set: {
                    ...b.set,
                    uploadProgress: progress
                  }
                };
              }
              return b;
            });
          });
        }
      });

      toast({
        title: "Upload successful",
        description: "Video has been uploaded successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      setUploadModalOpen(false);
      setSelectedBookingForUpload(null);
    } catch (error: any) {
      // Revert optimistic update on error
      queryClient.setQueryData(["bookings"], previousBookings);
      console.error("Upload error:", error);
      
      // Don't show error toast if user cancelled
      if (error?.message !== 'Upload cancelled') {
        toast({
          title: "Upload failed",
          description: error?.message || "An unexpected error occurred.",
          variant: "destructive",
        });
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      // Remove from active uploads
      setActiveUploads(prev => {
        const newUploads = { ...prev };
        delete newUploads[bookingId];
        return newUploads;
      });
    }
  };

  const openUploadModal = (booking: Booking) => {
    setSelectedBookingForUpload(booking);
    setUploadModalOpen(true);
  };

  const handleCancelUpload = async (bookingId: string | number) => {
    try {
      const uploader = getMultipartUploader();
      await uploader.cancelUpload({ type: "booking", bookingId: Number(bookingId) });
      
      // Revert the upload status in the UI
      queryClient.setQueryData(["bookings"], (old: any) => {
        if (!old) return old;
        return old.map((b: Booking) => {
          if (String(b.id) === String(bookingId)) {
            return {
              ...b,
              set: {
                ...b.set,
                uploadStatus: null,
                uploadProgress: null
              }
            };
          }
          return b;
        });
      });
      
      // Remove from active uploads
      setActiveUploads(prev => {
        const newUploads = { ...prev };
        delete newUploads[String(bookingId)];
        return newUploads;
      });
      
      toast({
        title: "Upload cancelled",
        description: "The video upload has been cancelled.",
      });
      
      // Invalidate to get fresh data
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
    } catch (error) {
      console.error("Error cancelling upload:", error);
      toast({
        title: "Error",
        description: "Failed to cancel upload",
        variant: "destructive",
      });
    }
  };

  const handleAddVideoLink = async (bookingId: number, videoUrl: string) => {
    // Optimistic update
    const previousBookings = queryClient.getQueryData(["bookings"]);
    queryClient.setQueryData(["bookings"], (old: any) => {
      if (!old) return old;
      return old.map((b: Booking) => {
        if (String(b.id) === String(bookingId)) {
          return {
            ...b,
            status: "completed",
            lifecycleStatus: BookingLifecycleStatus.POSTED,
            set: {
              ...b.set,
              publicVideoUrl: videoUrl,
              postedAt: new Date().toISOString()
            }
          };
        }
        return b;
      });
    });

    try {
      const response = await fetch(`/api/bookings/${bookingId}/video-url`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ videoUrl }),
      });

      if (!response.ok) {
        throw new Error("Failed to add video link");
      }

      toast({
        title: "Video link added",
        description: "The public video link has been added successfully.",
      });
      
      // Invalidate queries to ensure data consistency
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      
      // Close the modal
      setVideoLinkModalOpen(false);
      setSelectedBookingForVideoLink(null);
    } catch (error) {
      // Revert optimistic update on error
      queryClient.setQueryData(["bookings"], previousBookings);
      
      console.error("Error adding video link:", error);
      toast({
        title: "Failed to add video link",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
      throw error;
    }
  };

  const openVideoLinkModal = (booking: Booking) => {
    setSelectedBookingForVideoLink(booking);
    setVideoLinkModalOpen(true);
  };

  const handleBookingAction = (booking: Booking, action: 'approve' | 'reject' | 'delete') => {
    // Show confirmation dialog
    setConfirmDialog({
      open: true,
      action,
      bookingId: booking.id,
      djName: booking.dj.displayName || booking.dj.email || "Unknown DJ",
      bookingTime: `${VenueTimeFormats.dateOnly(booking.timeSlot.startTime)} at ${VenueTimeFormats.timeOnly(booking.timeSlot.startTime)}`
    });
  };

  const confirmBookingAction = async () => {
    if (!confirmDialog.bookingId || !confirmDialog.action) return;
    
    const bookingId = confirmDialog.bookingId;
    const action = confirmDialog.action;
    const actionKey = `${bookingId}-${action}`;
    
    setLoadingActions(prev => ({ ...prev, [actionKey]: true }));
    
    const statusMap = {
      approve: 'approved',
      reject: 'rejected',
      delete: 'delete'
    };

    const lifecycleStatusMap = {
      approve: BookingLifecycleStatus.SCHEDULED,
      reject: BookingLifecycleStatus.REJECTED,
      delete: 'delete'
    };

    // Optimistically update the cache
    const previousBookings = queryClient.getQueryData(["bookings"]);
    
    if (action === 'delete') {
      // Remove booking from cache
      queryClient.setQueryData(["bookings"], (old: any) => {
        if (!old) return old;
        return old.filter((b: Booking) => String(b.id) !== String(bookingId));
      });
    } else {
      // Update booking status in cache
      queryClient.setQueryData(["bookings"], (old: any) => {
        if (!old) return old;
        const updated = old.map((b: Booking) => {
          // Ensure we're comparing the same types (both as strings)
          const isMatch = String(b.id) === String(bookingId);
          if (isMatch) {
            console.log('Updating booking:', { 
              bookingId: b.id, 
              oldStatus: b.status, 
              newStatus: statusMap[action],
              oldLifecycleStatus: b.lifecycleStatus,
              newLifecycleStatus: lifecycleStatusMap[action]
            });
            return { 
              ...b, 
              status: statusMap[action],
              lifecycleStatus: lifecycleStatusMap[action]
            };
          }
          return b;
        });
        console.log('Optimistic update completed. Total bookings:', updated.length);
        return updated;
      });
    }

    try {
      if (action === 'delete') {
        // Handle delete separately
        const response = await fetch(`/api/bookings/${bookingId}`, {
          method: "DELETE",
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error("Failed to delete booking");
        }

        toast({
          title: "Booking deleted",
          description: "The booking has been deleted successfully.",
        });
      } else {
        // Handle approve/reject
        const response = await fetch(`/api/bookings/${bookingId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ 
            status: statusMap[action],
            lifecycleStatus: lifecycleStatusMap[action]
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to ${action} booking`);
        }

        toast({
          title: `Booking ${action}d`,
          description: `The booking has been ${action}d successfully.`,
        });
      }
      
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
    } catch (error) {
      // Revert optimistic update on error
      queryClient.setQueryData(["bookings"], previousBookings);
      
      console.error(`Error ${action}ing booking:`, error);
      toast({
        title: `Failed to ${action} booking`,
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    } finally {
      setLoadingActions(prev => {
        const newState = { ...prev };
        delete newState[actionKey];
        return newState;
      });
      // Close the confirmation dialog
      setConfirmDialog({
        open: false,
        action: null,
        bookingId: null,
        djName: "",
        bookingTime: ""
      });
    }
  };

  const handleReschedule = (booking: Booking) => {
    // Navigate to edit booking page
    setLocation(`/booking/${booking.id}/edit`);
  };

  const handleViewNotes = (booking: Booking) => {
    setSelectedBookingNotes({
      djName: booking.dj.displayName || booking.dj.email || "Unknown DJ",
      notes: booking.notes || "No notes/rider provided for this booking."
    });
    setNotesModalOpen(true);
  };

  if (isLoading) return <div className="min-h-screen bg-clubhouse-black text-white flex items-center justify-center">Loading...</div>;
  if (error) return <div className="min-h-screen bg-clubhouse-black text-white flex items-center justify-center">Error loading bookings</div>;

  return (
    <div className="min-h-screen bg-clubhouse-black text-white">
      <NavHeader />
      
      <div className="bg-clubhouse-black border-b border-white/10">
        <div className="container mx-auto px-4">
          <Tabs value={mainTab} onValueChange={(v) => setMainTab(v as "bookings" | "recordings")}>
            <TabsList className="bg-transparent p-0 h-auto gap-8 w-full justify-start border-0">
              <TabsTrigger 
                value="bookings" 
                className="bg-transparent px-0 pb-3 pt-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-white rounded-none text-lg font-medium"
              >
                Bookings
              </TabsTrigger>
              <TabsTrigger 
                value="recordings"
                className="bg-transparent px-0 pb-3 pt-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-white rounded-none text-lg font-medium"
              >
                Recordings
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      <main className="container mx-auto px-4 py-8 animate-page-in">
        {mainTab === "bookings" ? (
          <>
            {/* Bookings sub-tabs */}
            <div className="flex items-center gap-8 mb-8 border-b border-white/10">
              <button
                onClick={() => setBookingsTab("upcoming")}
                className={cn(
                  "pb-3 text-sm font-medium transition-colors border-b-2",
                  bookingsTab === "upcoming" ? "text-white border-white" : "text-white/40 hover:text-white border-transparent"
                )}
              >
                Upcoming
              </button>
              <button
                onClick={() => setBookingsTab("past")}
                className={cn(
                  "pb-3 text-sm font-medium transition-colors border-b-2",
                  bookingsTab === "past" ? "text-white border-white" : "text-white/40 hover:text-white border-transparent"
                )}
              >
                Past
              </button>
              <button
                onClick={() => setBookingsTab("all")}
                className={cn(
                  "pb-3 text-sm font-medium transition-colors border-b-2",
                  bookingsTab === "all" ? "text-white border-white" : "text-white/40 hover:text-white border-transparent"
                )}
              >
                All
              </button>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-4 mb-6">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  placeholder="Search by artist name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>

              <Select value={sortOrder} onValueChange={setSortOrder}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Sort By" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="soonest">Soonest</SelectItem>
                  <SelectItem value="furthest">Furthest</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Bookings Table */}
            <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] overflow-hidden border border-white/10">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/40 font-normal">Date & Time</TableHead>
                    <TableHead className="text-white/40 font-normal">Artist Name & Email</TableHead>
                    <TableHead className="text-white/40 font-normal">Duration</TableHead>
                    <TableHead className="text-white/40 font-normal">Notes</TableHead>
                    <TableHead className="text-white/40 font-normal">Links</TableHead>
                    <TableHead className="text-white/40 font-normal">Approval Status</TableHead>
                    <TableHead className="text-white/40 font-normal">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBookings.map((booking: Booking) => {
                    const status = getBookingStatus(booking);
                    return (
                      <TableRow key={booking.id} className="border-white/10">
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              {VenueTimeFormats.dateOnly(booking.timeSlot.startTime)}
                            </div>
                            <div className="text-sm text-white/40">
                              {VenueTimeFormats.timeOnly(booking.timeSlot.startTime)} - {' '}
                              {VenueTimeFormats.timeOnly(booking.timeSlot.endTime)}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{booking.dj.displayName || "No name"}</div>
                            <div className="text-sm text-white/40">{booking.dj.email}</div>
                            {booking.dj.phoneNumber && (
                              <div className="text-sm text-white/40">
                                {(() => {
                                  const phone = booking.dj.phoneNumber.replace(/\D/g, '');
                                  if (phone.length === 10) {
                                    // US number without country code
                                    return phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
                                  } else if (phone.length === 11 && phone.startsWith('1')) {
                                    // US number with country code
                                    return phone.replace(/1(\d{3})(\d{3})(\d{4})/, '+1 ($1) $2-$3');
                                  } else {
                                    // Keep original format for international numbers
                                    return booking.dj.phoneNumber;
                                  }
                                })()}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{booking.duration || 60} mins</TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-white/40 hover:text-white"
                            onClick={() => handleViewNotes(booking)}
                          >
                            View
                          </Button>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <TooltipProvider>
                              {booking.dj.instagramHandle && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      className="h-8 w-8"
                                      onClick={() => openLink(getInstagramUrl(booking.dj.instagramHandle))}
                                    >
                                      <Instagram className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Instagram</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              
                              {booking.dj.soundCloudLink && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      className="h-8 w-8"
                                      onClick={() => openLink(booking.dj.soundCloudLink)}
                                    >
                                      <Music className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>SoundCloud</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              
                              {booking.dj.mixLink && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      className="h-8 w-8"
                                      onClick={() => openLink(booking.dj.mixLink)}
                                    >
                                      <Music2 className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Mix/Spotify</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              
                              {(booking.causeLink || booking.set?.causeLink) && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      className="h-8 w-8"
                                      onClick={() => openLink(booking.set?.causeLink || booking.causeLink)}
                                    >
                                      <Heart className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{booking.set?.causeLabel || booking.causeName || 'Philanthropic Cause'}</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </TooltipProvider>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={cn("border-0", status.color)}>
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <TooltipProvider>
                              {booking.status !== "approved" && booking.status !== "completed" && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      onClick={() => handleBookingAction(booking, 'approve')}
                                      disabled={loadingActions[`${booking.id}-approve`]}
                                    >
                                      {loadingActions[`${booking.id}-approve`] ? (
                                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-400 border-t-transparent" />
                                      ) : (
                                        <Check className="h-4 w-4 text-emerald-400" />
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Approve</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {booking.status !== "rejected" && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      onClick={() => handleBookingAction(booking, 'reject')}
                                      disabled={loadingActions[`${booking.id}-reject`]}
                                    >
                                      {loadingActions[`${booking.id}-reject`] ? (
                                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-rose-400 border-t-transparent" />
                                      ) : (
                                        <XCircle className="h-4 w-4 text-rose-400" />
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Reject</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => handleReschedule(booking)}
                                  >
                                    <Calendar className="h-4 w-4 text-violet-400" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Edit Booking</p>
                                </TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => handleBookingAction(booking, 'delete')}
                                    disabled={loadingActions[`${booking.id}-delete`] || parseVenueTime(booking.timeSlot.startTime) < now}
                                  >
                                    {loadingActions[`${booking.id}-delete`] ? (
                                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-rose-400 border-t-transparent" />
                                    ) : (
                                      <Trash2 className={cn(
                                        "h-4 w-4",
                                        parseVenueTime(booking.timeSlot.startTime) < now ? "text-white/20" : "text-rose-400/70"
                                      )} />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{parseVenueTime(booking.timeSlot.startTime) < now ? "Cannot delete past bookings" : "Delete"}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <div className="p-4 text-sm text-white/40 border-t border-white/10 flex justify-between items-center">
                <span>Displaying 1-{filteredBookings.length} of {filteredBookings.length} results</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled>Previous</Button>
                  <Button variant="outline" size="sm">Next</Button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Recordings sub-tabs */}
            <div className="flex items-center gap-8 mb-8 border-b border-white/10">
              <button
                onClick={() => setRecordingsTab("all")}
                className={cn(
                  "pb-3 text-sm font-medium transition-colors border-b-2",
                  recordingsTab === "all" ? "text-white border-white" : "text-white/40 hover:text-white border-transparent"
                )}
              >
                All
              </button>
              <button
                onClick={() => setRecordingsTab("awaiting")}
                className={cn(
                  "pb-3 text-sm font-medium transition-colors border-b-2",
                  recordingsTab === "awaiting" ? "text-white border-white" : "text-white/40 hover:text-white border-transparent"
                )}
              >
                Awaiting Video
              </button>
              <button
                onClick={() => setRecordingsTab("artist-review")}
                className={cn(
                  "pb-3 text-sm font-medium transition-colors border-b-2",
                  recordingsTab === "artist-review" ? "text-white border-white" : "text-white/40 hover:text-white border-transparent"
                )}
              >
                Artist Review
              </button>
              <button
                onClick={() => setRecordingsTab("approved")}
                className={cn(
                  "pb-3 text-sm font-medium transition-colors border-b-2",
                  recordingsTab === "approved" ? "text-white border-white" : "text-white/40 hover:text-white border-transparent"
                )}
              >
                Ready to Post
              </button>
              <button
                onClick={() => setRecordingsTab("posted")}
                className={cn(
                  "pb-3 text-sm font-medium transition-colors border-b-2",
                  recordingsTab === "posted" ? "text-white border-white" : "text-white/40 hover:text-white border-transparent"
                )}
              >
                Posted
              </button>
            </div>

            {/* Search and Sort */}
            <div className="flex items-center gap-4 mb-6">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  placeholder="Search by artist name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              <Select value={sortOrder} onValueChange={setSortOrder}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Sort By" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="soonest">Soonest</SelectItem>
                  <SelectItem value="furthest">Furthest</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Recordings Table */}
            <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] overflow-hidden border border-white/10">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/40 font-normal w-[25%] pl-6">Date & Time</TableHead>
                    <TableHead className="text-white/40 font-normal w-[25%]">Artist Name & Email</TableHead>
                    <TableHead className="text-white/40 font-normal w-[25%]">Status</TableHead>
                    <TableHead className="text-white/40 font-normal w-[25%] pr-6">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBookings.map((booking: Booking) => {
                    const status = getRecordingStatus(booking);
                    return (
                      <TableRow key={booking.id} className="border-white/10">
                        <TableCell className="pl-6">
                          <div>
                            <div className="font-medium">
                              {VenueTimeFormats.dateOnly(booking.timeSlot.startTime)}
                            </div>
                            <div className="text-sm text-white/40">
                              {VenueTimeFormats.timeOnly(booking.timeSlot.startTime)} -
                              {VenueTimeFormats.timeOnly(booking.timeSlot.endTime)}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{booking.dj.displayName || "No name"}</div>
                            <div className="text-sm text-white/40">{booking.dj.email}</div>
                            {booking.dj.phoneNumber && (
                              <div className="text-sm text-white/40">
                                {(() => {
                                  const phone = booking.dj.phoneNumber.replace(/\D/g, '');
                                  if (phone.length === 10) {
                                    // US number without country code
                                    return phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
                                  } else if (phone.length === 11 && phone.startsWith('1')) {
                                    // US number with country code
                                    return phone.replace(/1(\d{3})(\d{3})(\d{4})/, '+1 ($1) $2-$3');
                                  } else {
                                    // Keep original format for international numbers
                                    return booking.dj.phoneNumber;
                                  }
                                })()}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={cn("border-0", status.color)}>
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="pr-6">
                          <div className="flex gap-2 items-center">
                            {/* Show loading state if upload is in progress */}
                            {booking.set?.uploadStatus === "uploading" ? (
                              <div className="flex items-center gap-2">
                                <div className="flex items-center gap-2 text-sm text-white/40">
                                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                  <span>Uploading{booking.set.uploadProgress ? ` ${booking.set.uploadProgress}%` : '...'}</span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-rose-400 hover:text-rose-300"
                                  onClick={() => handleCancelUpload(booking.id)}
                                  title="Cancel upload"
                                >
                                  <XOctagon className="h-4 w-4" />
                                </Button>
                              </div>
                            ) : status.label === "Awaiting Video" ? (
                              <Button 
                                className="bg-white hover:bg-white/90 text-black"
                                onClick={() => openUploadModal(booking)}
                              >
                                <Upload className="h-4 w-4 mr-2" />
                                Upload video
                              </Button>
                            ) : (
                              <div className="flex items-center gap-4">
                                {/* View/Review link for statuses with video (excluding Ready to Post and Posted) */}
                                {booking.set?.videoUrl && status.label !== "Ready to Post" && status.label !== "Posted" && (
                                  <button
                                    onClick={() => setLocation(`/review/${booking.id}`)}
                                    className="flex items-center gap-1.5 text-sm text-white/50 hover:text-white transition-colors"
                                  >
                                    <Eye className="h-3.5 w-3.5" />
                                    {status.label === "Artist Review" ? "View video" : "View details"}
                                  </button>
                                )}

                                {/* Review details link for Ready to Post and Posted */}
                                {(status.label === "Ready to Post" || status.label === "Posted") && (
                                  <button
                                    onClick={() => setLocation(`/admin/set-details/${booking.id}?from=${recordingsTab}`)}
                                    className="flex items-center gap-1.5 text-sm text-white/50 hover:text-white transition-colors"
                                  >
                                    <Eye className="h-3.5 w-3.5" />
                                    Review details
                                  </button>
                                )}

                                {/* YouTube external link for Posted */}
                                {status.label === "Posted" && booking.set?.publicVideoUrl && (
                                  <a
                                    href={booking.set.publicVideoUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1.5 text-sm text-white/30 hover:text-white/50 transition-colors"
                                  >
                                    <Youtube className="h-3.5 w-3.5" />
                                    YouTube
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <div className="p-4 text-sm text-white/40 border-t border-white/10 flex justify-between items-center">
                <span>Displaying 1-{filteredBookings.length} of {filteredBookings.length} results</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled>Previous</Button>
                  <Button variant="outline" size="sm">Next</Button>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
      
      {/* Upload Video Modal */}
      <UploadVideoModal
        isOpen={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          setSelectedBookingForUpload(null);
        }}
        booking={selectedBookingForUpload}
        onSave={handleVideoUpload}
        isUploading={isUploading}
        uploadProgress={uploadProgress}
      />
      
      {/* Notes/Rider Modal */}
      <Dialog open={notesModalOpen} onOpenChange={setNotesModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Notes for {selectedBookingNotes.djName}</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6 border border-white/10">
              <p className="text-white whitespace-pre-wrap">
                {selectedBookingNotes.notes}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => {
        if (!open) {
          setConfirmDialog({
            open: false,
            action: null,
            bookingId: null,
            djName: "",
            bookingTime: ""
          });
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog.action === 'approve' && 'Approve Booking'}
              {confirmDialog.action === 'reject' && 'Reject Booking'}
              {confirmDialog.action === 'delete' && 'Delete Booking'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.action === 'approve' && (
                <>
                  Are you sure you want to approve the booking for <strong>{confirmDialog.djName}</strong> on {confirmDialog.bookingTime}?
                  <br /><br />
                  The DJ will be notified and the time slot will be marked as booked.
                </>
              )}
              {confirmDialog.action === 'reject' && (
                <>
                  Are you sure you want to reject the booking for <strong>{confirmDialog.djName}</strong> on {confirmDialog.bookingTime}?
                  <br /><br />
                  The DJ will be notified of the rejection.
                </>
              )}
              {confirmDialog.action === 'delete' && (
                <>
                  Are you sure you want to delete the booking for <strong>{confirmDialog.djName}</strong> on {confirmDialog.bookingTime}?
                  <br /><br />
                  <span className="text-red-500 font-semibold">This action cannot be undone.</span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBookingAction}
              className={cn(
                confirmDialog.action === 'approve' && "bg-green-600 hover:bg-green-700",
                confirmDialog.action === 'reject' && "bg-orange-600 hover:bg-orange-700",
                confirmDialog.action === 'delete' && "bg-red-600 hover:bg-red-700"
              )}
            >
              {confirmDialog.action === 'approve' && 'Approve'}
              {confirmDialog.action === 'reject' && 'Reject'}
              {confirmDialog.action === 'delete' && 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}